import React from "react";

import {
  Card,
  CardContent,
  CardHeader,
} from "@replo/design-system/components/shadcn/core/card";
import twMerge from "@replo/design-system/utils/twMerge";

const SettingsContext = React.createContext<{
  maxWidth?: string;
}>({});

const SettingsCard: React.FC<
  React.PropsWithChildren<{
    className?: string;
    title?: string;
    description?: string;
    actions?: React.ReactNode;
  }>
> = ({ children, className, title, description, actions }) => {
  const { maxWidth } = React.useContext(SettingsContext);

  return (
    <Card
      className={twMerge(
        "bg-white h-fit p-5 w-full",
        maxWidth ?? "max-w-[650px]",
        className,
      )}
    >
      {(title ?? description ?? actions) && (
        <CardHeader className="flex-row items-center justify-between space-y-0 p-0">
          {(title ?? description) && (
            <div className="flex flex-col items-start">
              {title && <p className="header-h3 break-words">{title}</p>}
              {description && (
                <p className="text-sm text-muted break-words leading-relaxed">
                  {description}
                </p>
              )}
            </div>
          )}
          {actions && <div className="ml-auto">{actions}</div>}
        </CardHeader>
      )}
      <CardContent className="p-0">{children}</CardContent>
    </Card>
  );
};

type SettingsContentProps = {
  title?: string;
};

export const SettingsContent: React.FC<
  React.PropsWithChildren<SettingsContentProps>
> = ({ children, title }) => {
  return (
    <div className="flex flex-col gap-3 w-full">
      {title && (
        <span className="text-base font-medium break-words">{title}</span>
      )}
      {children}
    </div>
  );
};

export const SettingsLayout: React.FC<
  React.PropsWithChildren<{
    maxWidth?: string;
  }>
> = ({ children, maxWidth }) => {
  return (
    <SettingsContext.Provider value={{ maxWidth }}>
      <div className="flex flex-col gap-4 flex-1 items-center">{children}</div>
    </SettingsContext.Provider>
  );
};

type LoadedSettingsProps = {
  sections: {
    title?: string;
    description?: string;
    actions?: React.ReactNode;
    component: React.ReactNode;
    componentTitle?: string;
  }[];
};

export const LoadedSettings = ({ sections }: LoadedSettingsProps) => {
  return sections.map((section) => {
    return (
      <SettingsCard
        key={section.title}
        title={section.title}
        description={section.description}
        actions={section.actions}
      >
        <SettingsContent title={section.componentTitle}>
          {section.component}
        </SettingsContent>
      </SettingsCard>
    );
  });
};
